/**
 * UserInteractionWatcher - Keepalive Signal for Embedded Apps
 *
 * Purpose:
 * This component detects user interactions (touch events) within the embedded React Native app
 * and dispatches keepalive signals to the host app to prevent session timeout.
 *
 * Problem:
 * When the app runs embedded in a native host app, the host app's session management doesn't
 * recognize activity within the React Native view. This causes the host app's access token to
 * expire due to perceived inactivity, even when users are actively engaged.
 *
 * Solution:
 * Wraps the app in a touch event listener that dispatches throttled keepalive events to the
 * brownfield bridge, signaling to the host app that the user is active. The host app can then
 * refresh its access token to maintain the session.
 *
 * Usage:
 * Wrap your root component with this watcher in embedded mode only:
 * ```tsx
 * <UserInteractionWatcher>
 *   <YourApp />
 * </UserInteractionWatcher>
 * ```
 *
 * When running standalone (not embedded), this component is a no-op Fragment.
 */
import React, { PropsWithChildren, useCallback } from 'react'
import { useDispatch } from 'APP/Hooks/redux'
import styled from 'styled-components/native'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { userInteractionKeepAlive } from 'APP/Store/Brownfield/sagas'

const InteractionView = styled.View`
  flex: 1;
`

function EmbeddedUserInteractionWatcher({ ...rest }: PropsWithChildren<Record<string, unknown>>) {
  const dispatch = useDispatch()

  const updateKeepAliveActivity = useCallback(() => {
    dispatch(userInteractionKeepAlive())
  }, [dispatch])

  return <InteractionView onTouchStart={updateKeepAliveActivity} {...rest} />
}

const InteractionWatcher = (props: PropsWithChildren<Record<string, unknown>>) => {
  return isEmbeddedEnvironment() ? (
    <EmbeddedUserInteractionWatcher {...props} />
  ) : (
    <React.Fragment {...props} />
  )
}

export default InteractionWatcher
